import * as back from '~/components/layouts/Navigation/BreadcrumbContainer/functional/backLinks';
import { documentTemplateTitle } from '~/components/layouts/Navigation/BreadcrumbContainer/functional/documentTemplateTitle';
import { listBreadcrumb, when } from '~/components/layouts/Navigation/BreadcrumbContainer/functional/generators';
import * as match from '~/components/layouts/Navigation/BreadcrumbContainer/functional/matchers';
import type { RouteRule, BreadcrumbContext, BreadcrumbItem } from '~/components/layouts/Navigation/BreadcrumbContainer/functional/types';

const PRIORITY = {
  SECTION_BASE: 200,
  LIST_PAGE: 400,
  ADD_FORM: 500,
  EDIT_FORM: 510,
  DETAIL_PAGE: 520,
} as const;

const associationsBreadcrumb =
  (action: string, actionPath?: string, section?: string, sectionPath?: string) => (context: BreadcrumbContext) => {
    if (context.isMobile) {
      return [{ label: context.t(action), link: actionPath }];
    }

    return [
      { label: context.t('Associations') },
      { label: context.t(section ?? 'Violations'), link: sectionPath ?? '/associations/violations' },
      { label: context.t(action), link: actionPath },
    ];
  };

const communicationsBreadcrumb =
  (section: string, sectionPath: string, action?: string, actionPath?: string) => (context: BreadcrumbContext) => {
    const breadcrumbs: BreadcrumbItem[] = [{ label: context.t('Communications') }, { label: context.t(section), link: sectionPath }];

    if (action) {
      breadcrumbs.push({ label: context.t(action), ...(actionPath && { link: actionPath }) });
    }

    return breadcrumbs;
  };

export const otherRules: RouteRule[] = [
  {
    match: match.and(match.startsWith('/associations'), match.includes('/architectural-requests'), match.hasId()),
    generate: associationsBreadcrumb(
      'Architectural request',
      '/associations/architectural-requests',
      'Architectural requests',
      '/associations/architectural-requests'
    ),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/associations'), match.includes('/architectural-requests'), match.includes('/edit')),
    generate: associationsBreadcrumb(
      'Edit architectural request',
      '/associations/architectural-requests',
      'Architectural requests',
      '/associations/architectural-requests'
    ),
    backLink: back.goBack,
    priority: PRIORITY.EDIT_FORM,
  },
  {
    match: match.and(match.startsWith('/associations'), match.includes('/architectural-requests')),
    generate: listBreadcrumb('Associations', '/associations', 'Architectural requests', '/associations/architectural-requests'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/associations'), match.includes('/board-members')),
    generate: listBreadcrumb('Associations', '/associations', 'Board members', '/associations/board-members'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/associations'), match.includes('/violations'), match.includes('/edit')),
    generate: associationsBreadcrumb('Edit violation'),
    backLink: back.goBack,
    priority: PRIORITY.EDIT_FORM,
  },
  {
    match: match.and(match.startsWith('/associations'), match.includes('/violations'), match.hasId()),
    generate: associationsBreadcrumb('Violation detail'),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/associations'), match.includes('/violations'), match.includes('/add')),
    generate: associationsBreadcrumb('Add association violation'),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.startsWith('/associations'),
    generate: when(
      (context) => !context.isMobile,
      listBreadcrumb('Associations', '/associations', 'Violations', '/associations/violations'),
      () => []
    ),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/communications'), match.includes('/announcements'), match.includes('/add')),
    generate: communicationsBreadcrumb(
      'Announcements',
      '/communications/announcements',
      'New announcement',
      '/communications/announcements/add'
    ),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/communications'), match.includes('/announcements'), match.hasId()),
    generate: communicationsBreadcrumb('Announcements', '/communications/announcements', 'Announcement detail'),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/communications'), match.includes('/announcements')),
    generate: communicationsBreadcrumb('Announcements', '/communications/announcements'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/communications'), match.includes('/archive-messages'), match.hasId()),
    generate: communicationsBreadcrumb('Archived messages', '/communications/archive-messages', 'Archive message detail'),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/communications'), match.includes('/archive-messages')),
    generate: communicationsBreadcrumb('Archived messages', '/communications/archive-messages'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/communications'), match.includes('/document-templates'), match.includes('/edit')),
    generate: (context) => [
      { label: context.t('Communications') },
      { label: context.t('Document templates'), link: '/communications/document-templates' },
      { label: documentTemplateTitle() || context.t('Edit') },
    ],
    backLink: back.goBack,
    priority: PRIORITY.EDIT_FORM,
  },
  {
    match: match.and(match.startsWith('/communications'), match.includes('/document-templates')),
    generate: communicationsBreadcrumb('Document templates', '/communications/document-templates'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.startsWith('/communications'),
    generate: (context) => [{ label: context.t('Communications') }],
    backLink: back.homeLink,
    priority: PRIORITY.SECTION_BASE,
  },
];
